import type { ForwardedRef } from "react"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"

import type { AiContextAttachment } from "@productlane/lib/src/ai"
import { getDefaultExtensions } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"

import { composerDocToAttachments, composerDocToText } from "./context-mention"
import {
	ContextMention,
	ContextMentionPluginKey,
} from "./context-mention-extension"

export interface CopilotComposerHandle {
	clear: () => void
	focus: () => void
	isEmpty: () => boolean
	/** Appends a dictated phrase at the end, spaced off what is already there. */
	appendText: (text: string) => void
}

interface Props {
	placeholder: string
	autoFocus?: boolean
	className?: string
	onChange: (text: string, attachments: AiContextAttachment[]) => void
	onSubmit: () => void
	// A paste too long for the composer becomes a pill the caller owns.
	onLongPaste?: (text: string) => boolean
}

const COMPOSER_EXTENSIONS = getDefaultExtensions({
	image: false,
	imageGallery: false,
	heading: false,
	code: false,
	codeBlock: false,
	orderedList: false,
	link: false,
})

export const CopilotComposerEditor = forwardRef(
	(
		{
			placeholder,
			autoFocus,
			className,
			onChange,
			onSubmit,
			onLongPaste,
		}: Props,
		ref: ForwardedRef<CopilotComposerHandle>,
	) => {
		// useEditor builds its plugins once, so its handlers must read the latest
		// callbacks rather than the ones from the first render.
		const handlers = useRef({ onChange, onSubmit, onLongPaste })
		handlers.current = { onChange, onSubmit, onLongPaste }

		const editor = useEditor({
			autofocus: autoFocus ? "end" : false,
			extensions: [
				...COMPOSER_EXTENSIONS,
				ContextMention,
				Placeholder.configure({ placeholder }),
			],
			editorProps: {
				attributes: {
					class: cn(
						"max-h-[168px] overflow-y-auto px-2 py-1 text-sm font-medium tracking-tight text-primary outline-none",
						className,
					),
				},
				handlePaste: (_view, event) => {
					const pasted = event.clipboardData?.getData("text/plain") ?? ""
					if (!pasted) return false
					return handlers.current.onLongPaste?.(pasted) ?? false
				},
				handleKeyDown: (view, event) => {
					if (event.key !== "Enter" || event.shiftKey) return false
					// The suggestion list owns Enter while it is open, and its plugin
					// runs after this one.
					const suggestion = ContextMentionPluginKey.getState(view.state) as
						| { active?: boolean }
						| undefined
					if (suggestion?.active) return false
					event.preventDefault()
					handlers.current.onSubmit()
					return true
				},
			},
			onUpdate: ({ editor: instance }) => {
				const doc = instance.getJSON()
				handlers.current.onChange(
					composerDocToText(doc),
					composerDocToAttachments(doc),
				)
			},
		})

		useImperativeHandle(ref, () => ({
			clear: () => {
				if (!editor || editor.isEmpty) return
				editor.commands.clearContent(true)
				handlers.current.onChange("", [])
			},
			// The composer is pinned to the bottom of the panel, so the scroll pass
			// Tiptap runs after focus can only shove an ancestor that was never
			// meant to scroll. Safari hands it real scroll room after a tall card.
			focus: () => editor?.commands.focus("end", { scrollIntoView: false }),
			isEmpty: () => editor?.isEmpty ?? true,
			appendText: (text: string) => {
				if (!editor) return
				const addition = text.trim()
				if (!addition) return
				const needsSpace = !editor.isEmpty && !/\s$/.test(editor.getText())
				editor
					.chain()
					.focus("end", { scrollIntoView: false })
					.insertContent(needsSpace ? ` ${addition}` : addition)
					.run()
			},
		}))

		useEffect(() => {
			const placeholderExtension = editor?.extensionManager.extensions.find(
				(extension) => extension.name === "placeholder",
			) as { options: { placeholder: string } } | undefined
			if (!editor || !placeholderExtension) return
			placeholderExtension.options.placeholder = placeholder
			editor.view.dispatch(editor.state.tr)
		}, [editor, placeholder])

		if (!editor) return null

		return <EditorContent editor={editor} className="min-w-0 flex-grow" />
	},
)
